/**
 * Shared Jira-style priority icon (CDC-TMA T12).
 *
 * Priority 1-5 mapped to Jira-like directional icons + colours:
 *   1 Lowest  ⬇ blue (double down)
 *   2 Low     ↓ blue
 *   3 Medium  = orange
 *   4 High    ↑ red
 *   5 Highest ⬆ red (double up)
 *
 * PriorityGlyph renders the bare icon; PriorityDropdown is the shared editable
 * selector (icon + label, with a "Non définie" option), used by the card
 * (apiData binding), the fiche (dataLocation binding) and the list view.
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export const PRIORITY_META = {
  1: { label: 'Lowest', color: '#2563eb', shape: 'double-down' },
  2: { label: 'Low', color: '#3b82f6', shape: 'down' },
  3: { label: 'Medium', color: '#f59e0b', shape: 'equal' },
  4: { label: 'High', color: '#ef4444', shape: 'up' },
  5: { label: 'Highest', color: '#b91c1c', shape: 'double-up' },
};

const SHAPE_PATHS = {
  'up': ['M6 14l6-6 6 6'],
  'double-up': ['M6 11l6-6 6 6', 'M6 17l6-6 6 6'],
  'down': ['M6 10l6 6 6-6'],
  'double-down': ['M6 7l6 6 6-6', 'M6 13l6 6 6-6'],
  'equal': ['M5 9h14', 'M5 15h14'],
};

/**
 * The bare SVG glyph for a priority level (no interaction).
 */
export const PriorityGlyph = ({ level, size = 18 }) => {
  const meta = PRIORITY_META[level];
  if (!meta) {
    // Undefined priority → a neutral dash.
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round">
        <path d="M5 12h14" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {SHAPE_PATHS[meta.shape].map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
};

export const priorityLabel = (level) => PRIORITY_META[level]?.label ?? 'Non définie';

// 0 = "Non définie" (clear), then the 5 Jira levels.
const PRIORITY_OPTIONS = [0, 1, 2, 3, 4, 5];

/**
 * Editable priority selector: a glyph button that opens a dropdown listing the
 * 5 levels (icon + label) plus "Non définie". The menu is rendered in a portal
 * so it is never clipped by a scrolling Kanban column.
 *
 * Props:
 *   value      number (0-5)
 *   onChange   (level:number) => void
 *   size       glyph size in px (default 18)
 *   showLabel  also show the current level label next to the glyph
 *   readOnly   disables the dropdown
 */
export const PriorityDropdown = ({ value = 0, onChange, size = 18, showLabel = false, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const level = Number(value) || 0;

  const toggle = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (readOnly) return;
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    // Ignore clicks on the button itself and inside the portal menu — otherwise
    // mousedown closes (and unmounts) the menu before the option's click fires,
    // so the selection never applies.
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  const select = (lvl, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    onChange?.(lvl);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        onPointerDown={(e) => e.stopPropagation()}
        disabled={readOnly}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Priorité : ${priorityLabel(level)}`}
        className={`inline-flex items-center gap-1.5 ${readOnly ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}`}
      >
        <PriorityGlyph level={level} size={size} />
        {showLabel && <span className="text-sm text-gray-700">{priorityLabel(level)}</span>}
      </button>
      {open && coords && createPortal(
        <ul
          ref={menuRef}
          role="listbox"
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {PRIORITY_OPTIONS.map((lvl) => (
            <li
              key={lvl}
              role="option"
              aria-selected={lvl === level}
              onClick={(e) => select(lvl, e)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 ${lvl === level ? 'bg-iris-50 font-medium' : ''}`}
            >
              <PriorityGlyph level={lvl} size={16} />
              <span>{priorityLabel(lvl)}</span>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </>
  );
};
